import React from 'react';
import {
	Box,
	Modal as ChakraModal,
	HStack,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@chakra-ui/react';
import { Icon } from 'assets/Icon';
import { Button } from 'newui/components/Button';
import { ModalProps } from './Modal.types';

const Modal: React.FC<ModalProps> = ({
	isOpen,
	description,
	onClose,
	title,
	iconType = 'warning',
	customIcon,
	iconBgColor,
	iconColor,
	titleColor,
	children,
	primaryButton,
	secondaryButton,
	customFooter,
	closeOnOverlayClick = false,
	closeOnEsc = false,
	size = 'lg',
	isCentered = true,
	modalType = 'standard'
}) => {
	const getIconConfig = () => {
		if (customIcon) {
			return { icon: customIcon, bgColor: iconBgColor || 'gray.50', color: iconColor || 'gray.500' };
		}

		switch (iconType) {
			case 'error':
				return {
					icon: <Icon icon="toast-error" style={{ width: 50, height: 50 }} />,
					bgColor: 'red.50',
					color: 'red.500',
				};
			case 'warning':
				return {
					icon: <Icon icon="alert-triangle" style={{ width: 50, height: 50 }} />,
					bgColor: 'orange.50',
					color: 'orange.500',
				};
			case 'info':
				return {
					icon: <Icon icon="info" style={{ width: 50, height: 50 }} />,
					bgColor: 'blue.50',
					color: 'blue.500',
				};
			case 'success':
				return {
					icon: <Icon icon="check-circle" style={{ width: 50, height: 50 }} />,
					bgColor: 'green.50',
					color: 'green.500',
				};
			default:
				return {
					icon: null,
					bgColor: 'black.50',
					color: 'black.50',
				};
		}
	};

	const getTitleColor = () => {
		if (titleColor) {
			return titleColor;
		}

		if (customIcon) {
			return 'gray.800';
		}

		switch (iconType) {
			case 'error':
				return 'red.800';
			case 'warning':
				return 'yellow.600';
			case 'info':
				return 'blue.600';
			case 'success':
				return 'green.600';
			default:
				return 'gray.800';
		}
	};

	const iconConfig = getIconConfig();
	const computedTitleColor = getTitleColor();

	const maxWidthMap = {
		sm: '400px',
		md: '500px',
		lg: '600px',
		xl: '800px',
		xxl: '1200px',
	};

	const renderHeader = () => {
		return modalType === 'alert' ? <ModalHeader
			pt={8}
			pb={4}
			px={6}
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<VStack spacing={3} align="center">
				<Box p={3} borderRadius="full" bg={iconConfig.bgColor}>
					{React.isValidElement(iconConfig.icon) ? (
						React.cloneElement(iconConfig.icon as React.ReactElement, {
							stroke: iconConfig.color,
						})
					) : (
						iconConfig.icon
					)}
				</Box>
				<Text fontSize="2xl" fontWeight="bold" color={computedTitleColor} textAlign="center">
					{title}
				</Text>
				<Text>
					{description}
				</Text>
			</VStack>
		</ModalHeader> : <ModalHeader
			borderBottom="1px solid"
			borderColor="gray.100"
		>
			<HStack align={"left"} spacing={5}>
				<Box pt={2} borderRadius="full" bg={iconConfig.bgColor}>
					{React.isValidElement(iconConfig.icon) ? (
						React.cloneElement(iconConfig.icon as React.ReactElement, {
							stroke: iconConfig.color,
						})
					) : (
						iconConfig.icon
					)}
				</Box>
				<VStack spacing={0} align="left">

					<Text fontSize="2xl" fontWeight="bold" color={computedTitleColor}>
						{title}
					</Text>
					<Text fontSize="md" fontWeight="normal">
						{description}
					</Text>
				</VStack>
			</HStack>
		</ModalHeader>

	}

	const renderFooter = () => {
		if (customFooter) {
			return customFooter;
		}

		if (!primaryButton && !secondaryButton) {
			return null;
		}

		return (
			<>
				{primaryButton && (
					<Button
						onClick={primaryButton.onClick}
						btntype={primaryButton.btntype || 'primary'}
						size="lg"
						width="full"
						disabled={primaryButton.disabled}
						isLoading={primaryButton.isLoading}
					>
						{primaryButton.icon}
						{primaryButton.text}
					</Button>
				)}
				{secondaryButton && (
					<Button
						onClick={secondaryButton.onClick}
						btntype={secondaryButton.btntype || 'secondary'}
						size="lg"
						width="full"
						disabled={secondaryButton.disabled}
						isLoading={secondaryButton.isLoading}
					>
						{secondaryButton.icon}
						{secondaryButton.text}
					</Button>
				)}
			</>
		);
	};

	return (
		<ChakraModal
			isOpen={isOpen}
			onClose={onClose}
			isCentered={isCentered}
			closeOnOverlayClick={closeOnOverlayClick}
			closeOnEsc={closeOnEsc}
			motionPreset="scale"
			size={size}
		>
			<ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
			<ModalContent
				borderRadius="xl"
				bg="white"
				boxShadow="2xl"
				maxW={maxWidthMap[size]}
				mx="auto"
			>
				<ModalCloseButton className="z-[999]" size={"md"} />
				{renderHeader()}

				<ModalBody px={6} py={8}>
					<VStack spacing={6} align="stretch">
						{children}
					</VStack>
				</ModalBody>

				{(customFooter || primaryButton || secondaryButton) && (
					<ModalFooter px={6} pb={6} pt={4} flexDirection="column" gap={3}>
						{renderFooter()}
					</ModalFooter>
				)}
			</ModalContent>
		</ChakraModal>
	);
};

export default Modal;
